import { create } from 'zustand';

/**
 * Global Auto-Refresh Store
 *
 * Manages the global auto-refresh state across all dashboards.
 * - State persists in sessionStorage (survives navigation, not page refresh)
 * - Defaults to disabled
 * - Emits refresh triggers at configurable intervals (5s, 10s, 30s, 60s) when enabled
 * - Tracks the currently active dashboard
 */

const STORAGE_KEY = 'globalAutoRefresh';
const INTERVAL_STORAGE_KEY = 'globalAutoRefreshInterval';

export type RefreshInterval = 5 | 10 | 30 | 60;

// Helper to read auto-refresh state from sessionStorage
const getStoredAutoRefresh = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return sessionStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

// Helper to read refresh interval from sessionStorage
const getStoredInterval = (): RefreshInterval => {
  if (typeof window === 'undefined') return 60;
  try {
    const stored = sessionStorage.getItem(INTERVAL_STORAGE_KEY);
    if (stored) {
      const parsed = parseInt(stored, 10);
      if ([5, 10, 30, 60].includes(parsed)) {
        return parsed as RefreshInterval;
      }
    }
    return 60; // Default to 60 seconds
  } catch {
    return 60;
  }
};

// Helper to persist auto-refresh state to sessionStorage
const setStoredAutoRefresh = (enabled: boolean) => {
  if (typeof window === 'undefined') return;
  try {
    if (enabled) {
      sessionStorage.setItem(STORAGE_KEY, 'true');
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Ignore storage errors
  }
};

// Helper to persist refresh interval to sessionStorage
const setStoredInterval = (interval: RefreshInterval) => {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(INTERVAL_STORAGE_KEY, interval.toString());
  } catch {
    // Ignore storage errors
  }
};

export type DashboardId =
  | 'portfolio'
  | 'profit'
  | 'trade-groups'
  | 'trade-group-detail'
  | 'capital'
  | 'analytics'
  | 'market-overview'
  | 'volatility'
  | 'admin'
  | 'settings'
  | 'tax-reporting';

interface GlobalAutoRefreshState {
  // State
  isEnabled: boolean;
  activeDashboard: DashboardId | null;
  refreshTrigger: number; // Incremented to trigger refreshes
  intervalSeconds: RefreshInterval; // Configurable refresh interval in seconds

  // Actions
  setEnabled: (enabled: boolean) => void;
  setActiveDashboard: (dashboardId: DashboardId | null) => void;
  triggerRefresh: () => void;
  setIntervalSeconds: (interval: RefreshInterval) => void;
}

export const useGlobalAutoRefreshStore = create<GlobalAutoRefreshState>((set) => ({
  // Initial state - read from sessionStorage, default to disabled and 60s interval
  isEnabled: getStoredAutoRefresh(),
  activeDashboard: null,
  refreshTrigger: 0,
  intervalSeconds: getStoredInterval(),

  // Actions
  setEnabled: (enabled: boolean) => {
    setStoredAutoRefresh(enabled);
    set({ isEnabled: enabled });
  },

  setActiveDashboard: (dashboardId: DashboardId | null) =>
    set({ activeDashboard: dashboardId }),

  triggerRefresh: () =>
    set((state) => ({ refreshTrigger: state.refreshTrigger + 1 })),

  setIntervalSeconds: (interval: RefreshInterval) => {
    setStoredInterval(interval);
    set({ intervalSeconds: interval });
  },
}));

/**
 * Hook to subscribe to refresh triggers for a specific dashboard
 * Returns the refresh trigger counter that increments at the configured interval when:
 * 1. Global auto-refresh is enabled
 * 2. The specified dashboard is the active dashboard
 */
export const useAutoRefreshTrigger = (dashboardId: DashboardId): number => {
  const isEnabled = useGlobalAutoRefreshStore((state) => state.isEnabled);
  const activeDashboard = useGlobalAutoRefreshStore((state) => state.activeDashboard);
  const refreshTrigger = useGlobalAutoRefreshStore((state) => state.refreshTrigger);

  // Only return incrementing trigger if this dashboard is active and auto-refresh is enabled
  if (isEnabled && activeDashboard === dashboardId) {
    return refreshTrigger;
  }

  return 0;
};

/**
 * Global interval manager
 * Handles the interval that triggers refreshes when auto-refresh is enabled
 * The interval is dynamically adjusted based on the configured interval setting
 */
let globalIntervalId: NodeJS.Timeout | null = null;
let currentIntervalSeconds: RefreshInterval = 60;

export const startGlobalAutoRefreshInterval = () => {
  // Clear any existing interval
  if (globalIntervalId !== null) {
    clearInterval(globalIntervalId);
    globalIntervalId = null;
  }

  const setupInterval = () => {
    const state = useGlobalAutoRefreshStore.getState();
    const intervalMs = state.intervalSeconds * 1000;
    
    // Clear any existing interval before creating new one
    if (globalIntervalId !== null) {
      clearInterval(globalIntervalId);
      globalIntervalId = null;
    }
    
    currentIntervalSeconds = state.intervalSeconds;
    
    globalIntervalId = setInterval(() => {
      const currentState = useGlobalAutoRefreshStore.getState();
      if (currentState.isEnabled && currentState.activeDashboard !== null) {
        currentState.triggerRefresh();
      }
    }, intervalMs);
  };

  // Start initial interval
  setupInterval();

  // Subscribe to state changes and restart interval when interval changes
  const unsubscribe = useGlobalAutoRefreshStore.subscribe((state) => {
    // Only restart if interval has changed (prevents unnecessary restarts)
    if (state.intervalSeconds !== currentIntervalSeconds) {
      setupInterval();
    }
  });

  // Return cleanup function that clears interval and unsubscribes
  return () => {
    if (globalIntervalId !== null) {
      clearInterval(globalIntervalId);
      globalIntervalId = null;
    }
    unsubscribe();
  };
};

export const stopGlobalAutoRefreshInterval = () => {
  if (globalIntervalId !== null) {
    clearInterval(globalIntervalId);
    globalIntervalId = null;
  }
};

